/**
 * Draggable dashed preview lines for the draft SL/TP (up to two at once —
 * a trade can be missing both) and the draft pending order, extracted
 * from Chart.tsx. The DOM confirm-bars (volume/expiry/
 * Submit) live inline in Chart; these are just the SVG line + grab strip
 * that the bar's value follows. Body verbatim; closed-over refs became ctx.*.
 */
import type { MouseEvent, MutableRefObject, RefObject, ReactNode } from "react";
import type { IChartApi } from "lightweight-charts";
import type { ChartTheme } from "@/state/slices/uiSlice";
import type { AnySeries } from "./chartData";

export interface DraftRenderCtx {
  seriesRef: MutableRefObject<AnySeries | null>;
  chartRef: MutableRefObject<IChartApi | null>;
  containerRef: RefObject<HTMLDivElement>;
  theme: ChartTheme;
  startDraftDrag: (field?: "sl" | "tp") => (e: MouseEvent) => void;
  draftSlTp: {
    sl: number | null;
    tp: number | null;
    touched: { sl: boolean; tp: boolean };
  } | null;
  pendingDraft: { price: number } | null;
}

export function renderDraftSlTp(ctx: DraftRenderCtx): ReactNode {
  const d = ctx.draftSlTp;
  const series = ctx.seriesRef.current;
  const chart = ctx.chartRef.current;
  const container = ctx.containerRef.current;
  if (!d || !series || !chart || !container) return null;
  const psW = chart.priceScale("right").width();
  const w = Math.max(0, container.clientWidth - psW);

  // One line per level. Drawn at reduced opacity so it reads as PROPOSED,
  // not as a stop the broker is already holding — the whole point is that
  // nothing is live until Submit.
  const line = (field: "sl" | "tp", price: number) => {
    const yc = series.priceToCoordinate(price);
    if (yc == null) return null;
    const color = field === "sl" ? ctx.theme.slColor : ctx.theme.tpColor;
    // Chosen levels read stronger than the ones we merely proposed — the
    // chart alone should say which of the two lines will actually be sent.
    const chosen = d.touched[field];
    return (
      <g key={field} opacity={chosen ? 0.95 : 0.55}>
        <line
          x1={0}
          x2={w}
          y1={yc}
          y2={yc}
          stroke={color}
          strokeWidth={1.5}
          strokeDasharray="6 4"
          pointerEvents="none"
        />
        {/* Names the line without needing the confirm bar for reference,
            and marks it as draggable for someone who has never used it. */}
        <text
          x={8}
          y={yc - 5}
          fill={color}
          fontSize={10}
          fontWeight={600}
          pointerEvents="none"
        >
          {field === "sl" ? "SL" : "TP"}
        {ctx.draftSlTp?.touched[field] ? "" : " · drag to set"}
        </text>
        <rect
          data-trading-strip="1"
          x={0}
          y={yc - 5}
          width={w}
          height={10}
          fill="transparent"
          style={{ cursor: "ns-resize", pointerEvents: "all" }}
          onMouseDown={ctx.startDraftDrag(field)}
        />
      </g>
    );
  };

  return (
    <g>
      {d.sl != null && line("sl", d.sl)}
      {d.tp != null && line("tp", d.tp)}
    </g>
  );
}

export function renderPendingDraft(ctx: DraftRenderCtx): ReactNode {
  const d = ctx.pendingDraft;
  const series = ctx.seriesRef.current;
  const chart = ctx.chartRef.current;
  const container = ctx.containerRef.current;
  if (!d || !series || !chart || !container) return null;
  const yc = series.priceToCoordinate(d.price);
  if (yc == null) return null;
  const psW = chart.priceScale("right").width();
  const w = Math.max(0, container.clientWidth - psW);
  return (
    <g>
      <line
        x1={0}
        x2={w}
        y1={yc}
        y2={yc}
        stroke={ctx.theme.pendingOrder}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        pointerEvents="none"
      />
      <rect
        data-trading-strip="1"
        x={0}
        y={yc - 5}
        width={w}
        height={10}
        fill="transparent"
        style={{ cursor: "ns-resize", pointerEvents: "all" }}
        onMouseDown={ctx.startDraftDrag()}
      />
    </g>
  );
}
